// ./src/components/features/Artists/ArtistMembersEditor/ArtistMembersEditor.tsx
//
// Repeatable editor for an act's line-up (band members / panelists / a soloist).
// The full desired set is sent on save and diffed on `id` server-side, so existing
// rows keep their id. Mirrors InventoryBarcodesEditor — each row carries more fields
// (name, role, core flag, and an optional link to a contact), so a row is a small
// card rather than a single grid line.

import React, { useCallback } from "react";
import { Stack, Group, TextInput, Switch, ActionIcon, Tooltip, Button, Text, Box, Paper } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { ArtistMember, Person } from "@/utils/api";
import { AppIcon } from "@/components/generic";
import { PersonPicker } from "@/components/features/EventGuests/PersonPicker";

export interface ArtistMembersEditorProps {
  value: ArtistMember[];
  onChange: (members: ArtistMember[]) => void;
  disabled?: boolean;
  error?: string | null;
  /** Gate ⌘S while the person-picker's "create contact" drawer is open. */
  onCreateOpenChange?: (open: boolean) => void;
}

const emptyMember = (): ArtistMember => ({
  personId: null,
  nameTitle: null,
  firstName: "",
  lastName: "",
  email: null,
  phone: null,
  avatarUploadedFileId: null,
  memberRole: "",
  isCore: true,
});

export const ArtistMembersEditor: React.FC<ArtistMembersEditorProps> = ({
  value,
  onChange,
  disabled = false,
  error,
  onCreateOpenChange,
}) => {
  const { t } = useTranslation(["artists", "common"]);

  const updateAt = useCallback(
    (index: number, patch: Partial<ArtistMember>) => {
      onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    },
    [value, onChange]
  );

  const removeAt = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
    },
    [value, onChange]
  );

  const addMember = useCallback(() => {
    onChange([...value, emptyMember()]);
  }, [value, onChange]);

  // Link a member row to an existing contact. Person-override pattern: we do NOT
  // copy the name into the override fields — the display resolves LIVE from the
  // contact (so a rename never goes stale). We only stash the contact's CURRENT
  // values into the display* fields for immediate in-editor feedback this session;
  // the server recomputes them on read. Unlinking keeps any typed override.
  const linkPerson = useCallback(
    (index: number, person: Person | null) => {
      if (!person) {
        updateAt(index, {
          personId: null,
          displayFirstName: null,
          displayLastName: null,
          displayEmail: null,
          displayPhone: null,
          displayAvatarUploadedFileId: null,
        });

        return;
      }

      updateAt(index, {
        personId: person.id ?? null,
        displayFirstName: person.firstName ?? null,
        displayLastName: person.lastName ?? null,
        displayEmail: person.email ?? null,
        displayPhone: person.phone ?? null,
        displayAvatarUploadedFileId: person.avatarUploadedFileId ?? null,
      });
    },
    [updateAt]
  );

  return (
    <Stack gap="sm">
      {value.map((row, index) => (
        <Paper key={row.id ?? index} withBorder p="sm" radius="md">
          <Stack gap="sm">
            {/* Contact link FIRST — the identity of the member (and, when linked,
                the live source of the name). Remove sits on this row. */}
            <Group gap="xs" wrap="nowrap" align="flex-end">
              <Box style={{ flex: 1 }}>
                <PersonPicker
                  label={
                    row.personId
                      ? t("artists:members.fields.person.linkedLabel", { defaultValue: "Contact" })
                      : t("artists:members.fields.person.label", { defaultValue: "Link to a contact (optional)" })
                  }
                  placeholder={t("artists:members.fields.person.placeholder", { defaultValue: "Search contacts…" })}
                  value={row.personId ?? null}
                  onSelect={(person) => linkPerson(index, person)}
                  onCreateOpenChange={onCreateOpenChange}
                  disabled={disabled}
                  size="md"
                />
              </Box>
              <Tooltip label={t("common:buttons.remove", { defaultValue: "Remove" })} withArrow position="top">
                <ActionIcon
                  variant="subtle"
                  color="red"
                  mb={4}
                  onClick={() => removeAt(index)}
                  disabled={disabled}
                  aria-label={t("common:buttons.remove", { defaultValue: "Remove" })}
                >
                  <AppIcon iconName="Trash" size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>

            {/* Name inputs only for an unlinked (typed) member — a linked member's
                name comes from the contact above, so no duplicate field. */}
            {!row.personId && (
              <Group gap="sm" wrap="nowrap" align="flex-start">
                <TextInput
                  style={{ flex: 1 }}
                  label={t("artists:members.fields.firstName.label", { defaultValue: "First name" })}
                  placeholder={t("artists:members.fields.firstName.placeholder", { defaultValue: "First name" })}
                  value={row.firstName ?? ""}
                  onChange={(e) => updateAt(index, { firstName: e.currentTarget.value })}
                  disabled={disabled}
                  size="md"
                />
                <TextInput
                  style={{ flex: 1 }}
                  label={t("artists:members.fields.lastName.label", { defaultValue: "Last name" })}
                  placeholder={t("artists:members.fields.lastName.placeholder", { defaultValue: "Last name" })}
                  value={row.lastName ?? ""}
                  onChange={(e) => updateAt(index, { lastName: e.currentTarget.value })}
                  disabled={disabled}
                  size="md"
                />
              </Group>
            )}

            {/* Role / instrument — labeled for consistency with the other inputs. */}
            <TextInput
              label={t("artists:members.fields.memberRole.label", { defaultValue: "Role / instrument" })}
              placeholder={t("artists:members.fields.memberRole.placeholder", {
                defaultValue: "e.g. Lead vocals, Keys, Keynote",
              })}
              value={row.memberRole ?? ""}
              onChange={(e) => updateAt(index, { memberRole: e.currentTarget.value })}
              disabled={disabled}
              leftSection={<AppIcon iconName="Microphone" size={16} />}
              size="md"
            />

            {/* Core member — on its own line. */}
            <Switch
              label={t("artists:members.fields.isCore.label", { defaultValue: "Core member" })}
              description={t("artists:members.fields.isCore.description", {
                defaultValue: "In the regular line-up (vs a guest or substitute).",
              })}
              checked={row.isCore ?? false}
              onChange={(e) => updateAt(index, { isCore: e.currentTarget.checked })}
              disabled={disabled}
              size="md"
            />

            {row.personId ? (
              <Text size="sm" c="dimmed">
                {t("artists:members.usingContact", {
                  defaultValue: "This member's name comes from the linked contact and updates automatically if you rename them.",
                })}
              </Text>
            ) : null}
          </Stack>
        </Paper>
      ))}

      {value.length === 0 && (
        <Box>
          <Text size="md" c="dimmed">
            {t("artists:members.empty", {
              defaultValue: "No members yet — add the people who make up this act.",
            })}
          </Text>
        </Box>
      )}

      {error && (
        <Text size="md" c="red">
          {error}
        </Text>
      )}

      <Group>
        <Button
          variant="light"
          size="md"
          onClick={addMember}
          disabled={disabled}
          leftSection={<AppIcon iconName="Plus" size={14} />}
        >
          {t("artists:members.add", { defaultValue: "Add member" })}
        </Button>
      </Group>
    </Stack>
  );
};

export default ArtistMembersEditor;
